import { teammateMessageActionCardKind } from '@littlebird/action-cards';
import { ModalityType, type ActionCard, type Modality } from '@littlebird/sdk';
import { findLast } from 'lodash';

import { messageGroupOf } from './hivemind-conversations-status';

type ConversationMessageLike = {
  readonly id: string;
  readonly role: 'user' | 'assistant';
  readonly body: string;
  readonly modalitiesUsed: readonly Modality[] | null;
};

export type HivemindIncomingSender = {
  readonly senderId: number;
  readonly senderName: string;
  readonly sentAutomatically: boolean;
};

export function hivemindIncomingSenderOf(
  message: ConversationMessageLike
): HivemindIncomingSender | null {
  if (message.role !== 'user') return null;
  for (const modality of message.modalitiesUsed ?? []) {
    if (modality.type === ModalityType.HivemindIncoming) {
      return {
        senderId: modality.sender_id,
        senderName: modality.sender_name || 'Someone',
        // Messages from before automatic replies carry no flag
        sentAutomatically: modality.sent_automatically === true,
      };
    }
  }
  return null;
}

export function latestHivemindIncomingMessageId(
  messages: readonly ConversationMessageLike[]
): string | null {
  return (
    findLast(messages, (message) => hivemindIncomingSenderOf(message) !== null)
      ?.id ?? null
  );
}

export function hivemindAnswerAfterAssistantMessage(
  messages: readonly ConversationMessageLike[],
  assistantMessageId: string | null | undefined,
  teammateId: number
): {
  readonly answer: string | null;
  readonly messageId: string | null;
} | null {
  const assistantIndex = messages.findIndex(
    ({ id }) => id === assistantMessageId
  );
  if (assistantIndex < 0) return null;
  const answer = messages
    .slice(assistantIndex + 1)
    .find(
      (message) => hivemindIncomingSenderOf(message)?.senderId === teammateId
    );
  return { answer: answer?.body ?? null, messageId: answer?.id ?? null };
}

export function hivemindGroupAnswerIds(
  chatMessages: readonly ConversationMessageLike[],
  chatCards: readonly ActionCard[]
): ReadonlySet<string> {
  const answerIds = new Set<string>();
  const answersByAssistantMessageId = new Map<
    string,
    ReadonlyMap<number, string>
  >();

  const firstIncomingMessagesAfter = (
    assistantMessageId: string | null | undefined
  ): ReadonlyMap<number, string> | null => {
    if (!assistantMessageId) return null;
    const cached = answersByAssistantMessageId.get(assistantMessageId);
    if (cached) return cached;

    const assistantIndex = chatMessages.findIndex(
      ({ id }) => id === assistantMessageId
    );
    if (assistantIndex < 0) return null;

    const firstBySender = new Map<number, string>();
    for (let index = assistantIndex + 1; index < chatMessages.length; index++) {
      const message = chatMessages[index]!;
      const sender = hivemindIncomingSenderOf(message);
      if (sender && !firstBySender.has(sender.senderId)) {
        firstBySender.set(sender.senderId, message.id);
      }
    }
    answersByAssistantMessageId.set(assistantMessageId, firstBySender);
    return firstBySender;
  };

  for (const card of chatCards) {
    if (card.actionType !== teammateMessageActionCardKind.actionType) {
      continue;
    }
    const group = messageGroupOf(card, chatCards);
    const first = group[0];
    if (
      first?.id !== card.id ||
      group.filter(
        (member) =>
          !teammateMessageActionCardKind.decode(member.content)?.incoming_text
      ).length < 2
    ) {
      continue;
    }

    const firstBySender = firstIncomingMessagesAfter(first.assistantMessageId);
    if (!firstBySender) continue;

    for (const member of group) {
      const content = teammateMessageActionCardKind.decode(member.content);
      if (member.status !== 'completed' || !content || content.incoming_text) {
        continue;
      }
      const messageId = firstBySender.get(content.teammate.id);
      if (messageId) answerIds.add(messageId);
    }
  }

  return answerIds;
}
